<script setup>
  import { defineProps, ref, onMounted, computed } from 'vue'
  import InteractiveHistogram from '@/components/Charts/InteractiveHistogram.vue'
  import SankeyFlowChart from '@/components/Charts/SankeyFlowChart.vue'
  import FeedItem from '@/components/Feed/FeedItem.vue'
  import SubmissionComposer from '@/components/SubmissionComposer.vue'
  import SubmissionDetail from '@/components/SlideOver/components/SubmissionDetail.vue'
  import AnonymizationProviders from '@/components/IpDetails/AnonymizationProviders.vue'
  import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
  import OsintTimeline from '@/components/IpDetails/OsintTimeline.vue'
  import Ip from '@/api/Ip'
  import Events from '@/api/Events'
  import moment from 'moment'
  import { useRouter } from 'vue-router'
  import { useMainStore } from '@/stores/main'
  import { useUserStore } from '@/stores/user'
  import { getLogTypeColor } from '@/constants/logTypes'

  const router = useRouter()
  const mainStore = useMainStore()
  const userStore = useUserStore()

  const anonymizationProviders = ref([])
  const histogram = ref([])
  const histogramLoading = ref(false)
  // Sankey data - separate aggregations for accurate totals
  const sankeyInbound = ref({ portTotals: [], ipTotals: [], links: [] })
  const sankeyOutbound = ref({ portTotals: [], ipTotals: [], links: [] })
  const sankeyLoading = ref(false)
  // Log type toggle state for Sankey
  const availableLogTypes = ref([])  // Array of { log_type: string, count: number }
  const connectionLogTypes = ref([])  // Log types from connection-specific search (id.resp_h/id.orig_h)
  const connectionLogTypesLoading = ref(false)
  const selectedSankeyLogType = ref('conn')  // Default to conn
  const submissions = ref([])
  const dropdownOpen = ref(false)

  const props = defineProps({
    data: {
      type: Object,
      default: {}
    }
  });

  // Check if user has access to event search (requires Elite tier or higher)
  // accountType 4 = Elite, 5 = Enterprise, 1337 = Admin
  const hasEventAccess = computed(() => {
    return userStore.accountType >= 4 || userStore.accountType === 1337
  })

  // Fetch Sankey data for a specific log type
  async function fetchSankeyData(logType) {
    sankeyLoading.value = true

    const ipQuery = props.data.ip
    const inboundQuery = `id.resp_h:${ipQuery}`
    const outboundQuery = `id.orig_h:${ipQuery}`

    try {
      const [
        inboundPortsRes,
        inboundIpsRes,
        inboundLinksRes,
        outboundPortsRes,
        outboundIpsRes,
        outboundLinksRes
      ] = await Promise.all([
        // Inbound: traffic TO this IP
        Events.aggregate(inboundQuery, [logType], 'id.resp_p', ['terms'], 100),
        Events.aggregate(inboundQuery, [logType], 'id.orig_h', ['terms'], 100),
        Events.aggregate(inboundQuery, [logType], null, ['composite'], 300, null, ['id.orig_h', 'id.resp_p']),
        // Outbound: traffic FROM this IP
        Events.aggregate(outboundQuery, [logType], 'id.resp_p', ['terms'], 100),
        Events.aggregate(outboundQuery, [logType], 'id.resp_h', ['terms'], 100),
        Events.aggregate(outboundQuery, [logType], null, ['composite'], 300, null, ['id.resp_p', 'id.resp_h'])
      ])

      sankeyInbound.value = {
        portTotals: inboundPortsRes.status === 'success' ? (inboundPortsRes.data?.terms || []) : [],
        ipTotals: inboundIpsRes.status === 'success' ? (inboundIpsRes.data?.terms || []) : [],
        links: inboundLinksRes.status === 'success' ? (inboundLinksRes.data?.composite || []) : []
      }

      sankeyOutbound.value = {
        portTotals: outboundPortsRes.status === 'success' ? (outboundPortsRes.data?.terms || []) : [],
        ipTotals: outboundIpsRes.status === 'success' ? (outboundIpsRes.data?.terms || []) : [],
        links: outboundLinksRes.status === 'success' ? (outboundLinksRes.data?.composite || []) : []
      }
    } catch (err) {
      console.error('Failed to load sankey data:', err)
      sankeyInbound.value = { portTotals: [], ipTotals: [], links: [] }
      sankeyOutbound.value = { portTotals: [], ipTotals: [], links: [] }
    } finally {
      sankeyLoading.value = false
    }
  }

  // Handle log type toggle change
  function handleLogTypeToggle(logType) {
    if (logType !== selectedSankeyLogType.value) {
      selectedSankeyLogType.value = logType
      fetchSankeyData(logType)
    }
  }

  function getLabel(tag) {
    if (tag.includes("bruteforce") || tag.includes("malicious") || tag.includes("crime") || tag.includes("c2")) return "bad"
    else if (tag.includes("tor") || tag.includes("vpn") || tag.includes("anonymization") || tag.includes("proxy") || tag.includes("probing") || tag.includes("scanning")) return "warning"
  }

  const malicious = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return props.data.behavior.tags.includes("bruteforce") ||
          props.data.behavior.tags.includes("malicious") ||
          props.data.behavior.tags.includes("crime") ||
          props.data.behavior.tags.includes("c2")
  })

  const anonymization = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return props.data.behavior.tags.includes("tor") ||
          props.data.behavior.tags.includes("vpn") ||
          props.data.behavior.tags.includes("anonymization") ||
          props.data.behavior.tags.includes("proxy")
  })

  const suspicious = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return (props.data.behavior.tags.includes("scanning") ||
          props.data.behavior.tags.includes("probing")) && !malicious.value
  })

  function toggleDropdown() {
    dropdownOpen.value = !dropdownOpen.value
  }

  function closeDropdown() {
    dropdownOpen.value = false
  }

  function openExternalLink(url) {
    window.open(url, '_blank', 'noopener,noreferrer')
    closeDropdown()
    mainStore.close_slideover()
  }

  function navigateToProvider(provider) {
    if (provider.provider) {
      mainStore.close_slideover()
      router.push(`/providers/${provider.provider}`)
    }
  }

  // Click outside directive
  const vClickOutside = {
    mounted(el, binding) {
      el.clickOutsideEvent = function(event) {
        if (!(el === event.target || el.contains(event.target))) {
          binding.value(event)
        }
      }
      document.body.addEventListener('click', el.clickOutsideEvent)
    },
    unmounted(el) {
      document.body.removeEventListener('click', el.clickOutsideEvent)
    }
  }

  onMounted(async () => {
    const [anonProvidersRes, submissionsRes] = await Promise.all([
      Ip.IpAnonymizationProviders(props.data.ip),
      Ip.IpSubmissions(props.data.ip)
    ])

    anonymizationProviders.value = anonProvidersRes.data || []
    submissions.value = submissionsRes.data || []

    // Fetch histogram and sankey data for this IP (requires Elite tier or higher)
    // accountType 4 = Elite, 5 = Enterprise, 1337 = Admin
    if (hasEventAccess.value) {
      histogramLoading.value = true

      const ipQuery = props.data.ip

      // Fetch histogram (broad search across all fields mentioning this IP)
      Events.search(ipQuery, 0, 0, [])
        .then(histogramResponse => {
          if (histogramResponse.status === 'success') {
            histogram.value = histogramResponse.data.histogram || []
            const logTypes = histogramResponse.data.log_types || []
            availableLogTypes.value = logTypes.sort((a, b) => b.count - a.count)
          }
        })
        .catch(err => console.error('Failed to load histogram:', err))
        .finally(() => { histogramLoading.value = false })

      // Fetch connection-specific log types (narrow: only direct src/dst connections)
      // Then fetch Sankey data for the best available log type
      connectionLogTypesLoading.value = true
      Events.search(`id.resp_h:${ipQuery} OR id.orig_h:${ipQuery}`, 0, 0, [])
        .then(response => {
          if (response.status === 'success') {
            const logTypes = response.data.log_types || []
            connectionLogTypes.value = logTypes.sort((a, b) => b.count - a.count)

            // If 'conn' isn't available, select the first available type
            if (connectionLogTypes.value.length > 0 && !connectionLogTypes.value.find(lt => lt.log_type === 'conn')) {
              selectedSankeyLogType.value = connectionLogTypes.value[0].log_type
            }

            // Only fetch Sankey if there are connection log types
            if (connectionLogTypes.value.length > 0) {
              fetchSankeyData(selectedSankeyLogType.value)
            }
          }
        })
        .catch(err => console.error('Failed to load connection log types:', err))
        .finally(() => { connectionLogTypesLoading.value = false })
    }
  })

  // Navigation handlers
  // Handle histogram bar click (timestamp and interval are in milliseconds)
  function handleHistogramBarClick({ timestamp, interval }) {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip,
        start: timestamp.toString(),
        end: (timestamp + interval).toString()
      }
    })
  }

  // Handle histogram brush select (times are in milliseconds)
  function handleHistogramBrushSelect({ startTime, endTime }) {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip,
        start: startTime.toString(),
        end: endTime.toString()
      }
    })
  }

  function viewAllNetflowEvents() {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip
      }
    })
  }

  function handleSankeyNodeClick({ type, value, direction, excludeIps, excludePorts }) {
    mainStore.close_slideover()

    let query = ''
    const focusIp = props.data.ip

    if (type === 'other') {
      // "Other" node - could be "Other IPs" or "Other Ports"
      if (excludeIps && excludeIps.length > 0) {
        // "Other IPs" node
        const exclusions = excludeIps.map(ip => `id.${direction === 'source' ? 'orig' : 'resp'}_h:${ip}`).join(' OR ')
        if (direction === 'source') {
          query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
        } else {
          query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
        }
      } else if (excludePorts && excludePorts.length > 0) {
        // "Other Ports" node
        const exclusions = excludePorts.map(p => `id.${direction === 'source' ? 'resp' : 'orig'}_p:${p}`).join(' OR ')
        if (direction === 'source') {
          query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
        } else {
          query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
        }
      }
    } else if (type === 'ip') {
      if (direction === 'source') {
        query = `id.orig_h:${value} AND id.resp_h:${focusIp}`
      } else {
        query = `id.resp_h:${value} AND id.orig_h:${focusIp}`
      }
    } else if (type === 'port') {
      if (direction === 'source') {
        query = `id.resp_p:${value} AND id.resp_h:${focusIp}`
      } else {
        query = `id.resp_p:${value} AND id.orig_h:${focusIp}`
      }
    }

    if (query) {
      router.push({
        name: 'EventSearch',
        query: { query, logType: selectedSankeyLogType.value }
      })
    }
  }

  function handleSankeyEdgeClick({ source, target }) {
    mainStore.close_slideover()

    let query = ''
    const focusIp = props.data.ip

    if (!source || !target) return

    // Determine if we're dealing with inbound (source direction) or outbound (dest direction)
    const isInbound = source.direction === 'source' || target.direction === 'source'

    // Helper to build exclusion clause
    const buildIpExclusion = (ips, field) => ips.map(ip => `${field}:${ip}`).join(' OR ')
    const buildPortExclusion = (ports, field) => ports.map(p => `${field}:${p}`).join(' OR ')

    if (isInbound) {
      // Inbound edges: IP -> Port or Port -> Center
      if (source.type === 'ip' && target.type === 'port') {
        query = `id.orig_h:${source.value} AND id.resp_p:${target.value} AND id.resp_h:${focusIp}`
      } else if (source.type === 'ip' && target.type === 'other' && target.excludePorts) {
        // IP -> Other Ports
        const exclusions = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.orig_h:${source.value} AND id.resp_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludeIps && target.type === 'port') {
        // Other IPs -> Port
        const exclusions = buildIpExclusion(source.excludeIps, 'id.orig_h')
        query = `id.resp_p:${target.value} AND id.resp_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludeIps && target.type === 'other' && target.excludePorts) {
        // Other IPs -> Other Ports
        const ipExcl = buildIpExclusion(source.excludeIps, 'id.orig_h')
        const portExcl = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.resp_h:${focusIp} AND NOT (${ipExcl}) AND NOT (${portExcl})`
      } else if (source.type === 'port' && target.direction === 'center') {
        query = `id.resp_p:${source.value} AND id.resp_h:${focusIp}`
      } else if (source.type === 'other' && source.excludePorts && target.direction === 'center') {
        // Other Ports -> Center
        const exclusions = buildPortExclusion(source.excludePorts, 'id.resp_p')
        query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
      }
    } else {
      // Outbound edges: Center -> Port or Port -> IP
      if (source.direction === 'center' && target.type === 'port') {
        query = `id.orig_h:${focusIp} AND id.resp_p:${target.value}`
      } else if (source.direction === 'center' && target.type === 'other' && target.excludePorts) {
        // Center -> Other Ports
        const exclusions = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'port' && target.type === 'ip') {
        query = `id.orig_h:${focusIp} AND id.resp_p:${source.value} AND id.resp_h:${target.value}`
      } else if (source.type === 'port' && target.type === 'other' && target.excludeIps) {
        // Port -> Other IPs
        const exclusions = buildIpExclusion(target.excludeIps, 'id.resp_h')
        query = `id.orig_h:${focusIp} AND id.resp_p:${source.value} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludePorts && target.type === 'ip') {
        // Other Ports -> IP
        const exclusions = buildPortExclusion(source.excludePorts, 'id.resp_p')
        query = `id.resp_h:${target.value} AND id.orig_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludePorts && target.type === 'other' && target.excludeIps) {
        // Other Ports -> Other IPs
        const portExcl = buildPortExclusion(source.excludePorts, 'id.resp_p')
        const ipExcl = buildIpExclusion(target.excludeIps, 'id.resp_h')
        query = `id.orig_h:${focusIp} AND NOT (${portExcl}) AND NOT (${ipExcl})`
      }
    }

    if (query) {
      router.push({
        name: 'EventSearch',
        query: { query, logType: selectedSankeyLogType.value }
      })
    }
  }

  // Get tags from OSINT service
  function getServiceTags(service) {
    if (!service.tags || !Array.isArray(service.tags)) return []
    return service.tags
  }

  // Filter submissions to only show those with text content
  const submissionsWithContent = computed(() => {
    return submissions.value.filter(sub => sub.content && sub.content.trim() !== '')
  })

  function openSubmissionModal() {
    mainStore.show_modal(SubmissionComposer, {
      lockedItems: [props.data.ip],
      hideItems: false,
      placeholder: `Share information about ${props.data.ip}...`,
      defaultChannel: 1  // Intel channel
    })
  }

  async function refreshSubmissions() {
    const submissionsRes = await Ip.IpSubmissions(props.data.ip)
    submissions.value = submissionsRes.data || []
  }

  function handleSubmissionCommentClick(submission) {
    mainStore.show_modal(SubmissionComposer, {
      parentSubmission: {
        ...submission,
        id: submission.submission_id  // Map submission_id to id for SubmissionComposer
      },
      hideItems: true,
      placeholder: `Reply to ${submission.user.name}...`,
      defaultChannel: 1  // Intel channel
    })
  }

  function handleSubmissionClick(submission) {
    mainStore.show_slideover(
      SubmissionDetail,
      { submissionId: submission.submission_id },
      { width: 50 },
      'Submission'
    )
  }

  function handleAttachmentClick(submission) {
    mainStore.show_slideover(
      SubmissionDetail,
      { submissionId: submission.submission_id, activeTab: 'attachments' },
      { width: 50 },
      'Submission'
    )
  }

  function handleDataClick(submission) {
    mainStore.show_slideover(
      SubmissionDetail,
      { submissionId: submission.submission_id, activeTab: 'data' },
      { width: 50 },
      'Submission'
    )
  }

  // Listen for submission created events
  mainStore.event_on('submission-created', () => {
    refreshSubmissions()
  })
</script>

<template>
  <div class="ip-view-container">
    <div class="container pt-5">
      <!-- IP Header Section -->
      <div class="ip-header" data-guide="ip-header">
        <div class="ip-header-content">
          <h1 class="ip-address">{{ props.data.ip }}</h1>
          <div class="location-info" v-if="props.data.location">
            <img
              height="16"
              v-if="props.data.location.country_iso != ''"
              class="country-flag"
              :src="require('@/assets/flags/4x3/' + props.data.location.country_iso.toLowerCase() + '.svg')"
            >
            <span class="location-text">
              <span v-if="props.data.location.city">{{ props.data.location.city }}, </span>
              {{ props.data.location.country_iso }}
            </span>
          </div>
        </div>
        <div class="ip-header-actions">
          <div class="open-in-dropdown" v-click-outside="closeDropdown">
            <button class="btn-open-in" @click="toggleDropdown">
              Open in
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 5L6 8L9 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="dropdown-menu" :class="{ 'show': dropdownOpen }">
              <button @click="openExternalLink(`https://www.shodan.io/host/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">🔍</span>
                Shodan
              </button>
              <button @click="openExternalLink(`https://www.virustotal.com/gui/ip-address/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">🛡️</span>
                VirusTotal
              </button>
              <button @click="openExternalLink(`https://www.abuseipdb.com/check/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">⚠️</span>
                AbuseIPDB
              </button>
              <button @click="openExternalLink(`https://censys.io/ipv4/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">📊</span>
                Censys
              </button>
              <button @click="openExternalLink(`https://otx.alienvault.com/indicator/ip/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">👽</span>
                AlienVault OTX
              </button>
              <button @click="openExternalLink(`https://urlscan.io/ip/${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">🌐</span>
                URLScan.io
              </button>
              <button @click="openExternalLink(`https://www.threatcrowd.org/ip.php?ip=${props.data.ip}`)" class="dropdown-item">
                <span class="dropdown-icon">🔬</span>
                ThreatCrowd
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Infrastructure Card -->
      <div class="top-cards-grid" data-guide="ip-cards">
        <div class="info-card infrastructure-card">
          <div class="card-header">
            <span class="card-label">Infrastructure</span>
            <h3 class="card-title">{{ props.data.company?.name || 'Unknown' }}</h3>
          </div>
          <p class="card-description">{{ props.data.company?.description || 'No description available' }}</p>

          <!-- Network Details -->
          <div class="infrastructure-details">
            <div class="detail-row" v-if="props.data.as && props.data.as.as_number">
              <span class="detail-label">AS Number</span>
              <span class="detail-value">
                AS{{ props.data.as.as_number }}
              </span>
            </div>
            <div class="detail-row" v-if="props.data.as && props.data.as.organization">
              <span class="detail-label">Organization</span>
              <span class="detail-value">{{ props.data.as.organization }}</span>
            </div>
            <div class="detail-row" v-if="props.data.network && props.data.network.type">
              <span class="detail-label">Network Type</span>
              <span class="detail-value">{{ props.data.network.type }}</span>
            </div>
            <div class="detail-row" v-if="props.data.network && props.data.network.range">
              <span class="detail-label">Network Range</span>
              <span class="detail-value">{{ props.data.network.range }}</span>
            </div>
            <div class="detail-row" v-if="props.data.operator">
              <span class="detail-label">Operator</span>
              <span class="detail-value">{{ props.data.operator }}</span>
            </div>
            <div class="detail-row" v-if="props.data.network_provider">
              <span class="detail-label">Network Provider</span>
              <span class="detail-value">{{ props.data.network_provider }}</span>
            </div>
          </div>
        </div>

        <!-- Anonymization Providers Card -->
        <div v-if="anonymizationProviders && anonymizationProviders.length > 0" class="info-card anonymization-card">
          <div class="card-header">
            <span class="card-label">Anonymization</span>
            <!--<h3 class="card-title">{{ providers.length }} Provider{{ providers.length !== 1 ? 's' : '' }}</h3>-->
          </div>

          <!-- Provider List -->
          <div class="providers-compact-list">
            <div
              v-for="(provider, index) in anonymizationProviders"
              :key="index"
              class="provider-compact-item"
            >
              <ProviderThumbnail
                :data="{
                  name: provider.display_name || provider.provider,
                  thumbnail_url: provider.thumbnail_url
                }"
              />
              <div class="provider-compact-info">
                <span class="provider-compact-name">{{ provider.display_name || provider.provider }}</span>
                <span class="provider-compact-meta">{{ provider.type }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- OSINT Providers Card -->
        <div v-if="props.data.osint && props.data.osint.services && props.data.osint.services.length > 0" class="info-card osint-card">
          <div class="card-header">
            <span class="card-label">OSINT</span>
          </div>

          <!-- Provider List -->
          <div class="providers-compact-list">
            <div
              v-for="service in props.data.osint.services"
              :key="service.provider"
              class="provider-compact-item"
            >
              <ProviderThumbnail
                :data="{
                  name: service.display_name || service.provider,
                  thumbnail_url: service.thumbnail_url
                }"
              />
              <div class="provider-compact-info">
                <div class="provider-name-line">
                  <span class="provider-compact-name">{{ service.display_name || service.provider }}</span>
                  <div class="provider-tags ms-auto" v-if="getServiceTags(service).length > 0">
                    <span
                      v-for="tag in getServiceTags(service)"
                      :key="tag"
                      class="provider-tag"
                      :class="getLabel(tag)"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>
                <span class="provider-compact-meta" v-if="service.last_seen">
                  {{ moment(service.last_seen).fromNow() }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Netflow Observations Section -->
      <section v-if="hasEventAccess" class="content-section" data-guide="ip-netflow">
        <div class="section-header">
          <div class="section-header-left">
            <h2 class="section-title">Netflow observations</h2>
            <span class="section-subtitle">Events mentioning this IP across all fields and log types</span>
          </div>
          <a @click.prevent="viewAllNetflowEvents" class="view-all-link">View all</a>
        </div>

        <!-- Loading state -->
        <div v-if="histogramLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading activity data...</p>
        </div>

        <!-- Histogram -->
        <InteractiveHistogram
          v-else-if="histogram && histogram.length > 0"
          :data="histogram"
          @bar-click="handleHistogramBarClick"
          @brush-select="handleHistogramBrushSelect"
          height="250px"
        />

        <!-- Empty state -->
        <div v-else class="empty-state-netflow">
          <p>No netflow available</p>
        </div>
      </section>

      <!-- Network Connections Sankey Chart Section -->
      <section v-if="hasEventAccess" class="content-section" data-guide="ip-connections">
        <div class="section-header">
          <div class="section-header-left">
            <h2 class="section-title">Network connections</h2>
            <span class="section-subtitle">Direct source and destination connections to and from this IP</span>
          </div>
          <a @click.prevent="viewAllNetflowEvents" class="view-all-link">View all</a>
        </div>

        <!-- Log Type Toggles (from connection-specific search for accurate counts) -->
        <div v-if="connectionLogTypes.length > 0 || connectionLogTypesLoading" class="log-type-toggles">
          <!-- Show conn toggle during initial loading -->
          <button
            v-if="connectionLogTypes.length === 0 && connectionLogTypesLoading"
            class="log-type-toggle active"
            disabled
          >
            <span class="log-type-color" :style="{ backgroundColor: getLogTypeColor('conn') }"></span>
            <span class="log-type-name">conn</span>
          </button>
          <!-- Show log types from connection-specific search -->
          <button
            v-for="lt in connectionLogTypes"
            :key="lt.log_type"
            class="log-type-toggle"
            :class="{ active: selectedSankeyLogType === lt.log_type }"
            @click="handleLogTypeToggle(lt.log_type)"
          >
            <span class="log-type-color" :style="{ backgroundColor: getLogTypeColor(lt.log_type) }"></span>
            <span class="log-type-name">{{ lt.log_type }}</span>
            <span class="log-type-count">{{ lt.count.toLocaleString() }}</span>
          </button>
        </div>

        <!-- Loading state -->
        <div v-if="sankeyLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading network flow data...</p>
        </div>

        <!-- Sankey Chart -->
        <SankeyFlowChart
          v-else-if="sankeyInbound.portTotals.length > 0 || sankeyOutbound.portTotals.length > 0"
          :inbound="sankeyInbound"
          :outbound="sankeyOutbound"
          :centeredIp="props.data.ip"
          :maxPorts="10"
          :maxIps="20"
          height="400px"
          @node-click="handleSankeyNodeClick"
          @edge-click="handleSankeyEdgeClick"
        />

        <!-- Empty state: no connection data, but observations exist -->
        <div v-else-if="connectionLogTypes.length === 0 && !connectionLogTypesLoading && availableLogTypes.length > 0" class="empty-state-netflow">
          <p>No direct network connections found</p>
          <p class="empty-state-hint">Netflow observations for this IP are likely from non-connection fields such as DNS answers, HTTP headers, or certificate data</p>
        </div>

        <!-- Empty state: no connection data at all -->
        <div v-else-if="connectionLogTypes.length === 0 && !connectionLogTypesLoading" class="empty-state-netflow">
          <p>No network connection data available</p>
        </div>

        <!-- Empty state: current log type has no Sankey data but others might -->
        <div v-else class="empty-state-netflow">
          <p>No data for {{ selectedSankeyLogType }} connections</p>
        </div>
      </section>

      <!-- Netflow & Network Connections Upgrade Prompt -->
      <section v-if="!hasEventAccess" class="content-section">
        <div class="section-header">
          <h2 class="section-title">Netflow observations</h2>
        </div>
        <div class="upgrade-prompt">
          <font-awesome-icon :icon="['fas', 'lock']" class="upgrade-icon" />
          <p class="upgrade-message">Netflow observations require an Elite or Enterprise plan.</p>
        </div>
      </section>

      <section v-if="!hasEventAccess" class="content-section">
        <div class="section-header">
          <h2 class="section-title">Network connections</h2>
        </div>
        <div class="upgrade-prompt">
          <font-awesome-icon :icon="['fas', 'lock']" class="upgrade-icon" />
          <p class="upgrade-message">Network connections require an Elite or Enterprise plan.</p>
        </div>
      </section>

      <!-- Comments Section -->
      <section class="content-section">
        <div class="section-header">
          <h2 class="section-title">Comments</h2>
          <a @click.prevent="openSubmissionModal" class="add-comment-btn">Add Comment</a>
        </div>
        <div class="submissions-list">
          <div v-if="submissionsWithContent.length === 0" class="empty-state-submissions">
            <p>No comments available</p>
          </div>
          <FeedItem
            v-for="submission in submissionsWithContent"
            :key="submission.id"
            :submission="submission"
            @comment-click="handleSubmissionCommentClick"
            @submission-click="handleSubmissionClick"
            @attachment-click="handleAttachmentClick"
            @data-click="handleDataClick"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap');

/* Container - No background, clean like Providers.vue */
.ip-view-container {
  min-height: 100vh;
  color: #E5E7EB;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* IP Header Section */
.ip-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.5rem;
}

.ip-header-content {
  flex: 1;
}

.ip-address {
  font-size: 2rem;
  font-weight: 600;
  color: #FFFFFF;
  margin: 0 0 0.5rem 0;
  font-family: 'Jura', sans-serif;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9CA3AF;
  font-size: 0.95rem;
}

.country-flag {
  border-radius: 3px;
}

.ip-header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn-view-stats,
.btn-open-in {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-view-stats:hover,
.btn-open-in:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

/* Open In Dropdown */
.open-in-dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background-color: #1B2029;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 0.5rem;
  min-width: 200px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  z-index: 1000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  pointer-events: none;
}

.dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  color: #E5E7EB;
  background: none;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.15s ease;
  white-space: nowrap;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-family: inherit;
}

.dropdown-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #FFFFFF;
}

.dropdown-icon {
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
}

/* Top Cards Grid - Like the design image */
.top-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.info-card {
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 1.5rem;
}

.card-header {
  margin-bottom: 1rem;
}

.card-label {
  display: block;
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #FFFFFF;
  margin: 0;
}

.card-description {
  color: #9CA3AF;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.card-links {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.card-link {
  color: #6B7280;
  font-size: 0.875rem;
  text-decoration: underline;
  transition: color 0.2s;
}

.card-link:hover {
  color: #E5E7EB;
}

.card-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.info-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: #9CA3AF;
}

/* Content Sections - Minimal styling, no boxes */
.content-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #FFFFFF;
  margin: 0;
  font-family: 'Jura', sans-serif;
}

.section-header-left {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.section-subtitle {
  color: #6B7280;
  font-size: 0.8rem;
  margin: 0;
}

.view-all-link,
.add-comment-btn {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.view-all-link:hover,
.add-comment-btn:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

/* Details List - Simple, no box */
.details-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #6B7280;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: right;
}

/* Behavior Tags */
.behavior-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.behavior-tag {
  display: inline-block;
  padding: 0.35rem 0.85rem;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  background-color: rgba(255, 255, 255, 0.05);
  color: #9CA3AF;
  transition: all 0.2s;
}

.behavior-tag.warning {
  background-color: rgba(252, 236, 136, 0.1);
  color: #fcec88;
}

.behavior-tag.bad {
  background-color: rgba(252, 140, 136, 0.1);
  color: #fc8c88;
}

/* Ports List - Clean, minimal */
.ports-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.port-item {
  display: grid;
  grid-template-columns: 80px 100px 1fr;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.9rem;
}

.port-item:last-child {
  border-bottom: none;
}

.port-number {
  color: #FFFFFF;
  font-weight: 500;
}

.port-protocol {
  color: #9CA3AF;
}

.port-description {
  color: #6B7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Infrastructure Details within card */
.infrastructure-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .detail-label {
  color: #6B7280;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-row .detail-value {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: right;
}

/* Responsive */
@media (max-width: 768px) {
  .ip-header {
    flex-direction: column;
    gap: 1rem;
  }

  .ip-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .btn-view-stats,
  .btn-open-in {
    flex: 1;
  }

  .dropdown-menu {
    right: auto;
    left: 0;
  }

  .top-cards-grid {
    grid-template-columns: 1fr;
  }

  .port-item {
    grid-template-columns: 60px 80px 1fr;
    gap: 0.5rem;
  }
}

/* Anonymization Card - Compact Provider List */
.providers-compact-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.provider-compact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 0.5rem;
}

.provider-compact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.provider-name-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.provider-compact-name {
  font-size: 0.9rem;
  color: #E5E7EB;
  font-weight: 500;
}

.provider-compact-meta {
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
}

.provider-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
}

.provider-tag {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  background-color: rgba(255, 255, 255, 0.05);
  color: #9CA3AF;
  transition: all 0.2s;
}

.provider-tag.warning {
  background-color: rgba(252, 236, 136, 0.1);
  color: #fcec88;
}

.provider-tag.bad {
  background-color: rgba(252, 140, 136, 0.1);
  color: #fc8c88;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6B7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #374151;
  border-top: 3px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  margin: 0;
  font-size: 1rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6B7280;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Empty State for Netflow */
.empty-state-netflow {
  text-align: center;
  padding: 3rem;
  color: #6B7280;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 0.75rem;
}

.empty-state-netflow p {
  margin: 0;
  font-size: 0.95rem;
}

.empty-state-hint {
  margin-top: 0.5rem !important;
  font-size: 0.8rem !important;
  color: #4B5563;
}

/* Section Count */
.section-count {
  color: #6B7280;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Submissions List */
.submissions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.submissions-list .empty-state-submissions {
  text-align: center;
  padding: 2rem;
  color: #6B7280;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 0.75rem;
}

.submissions-list .empty-state-submissions p {
  margin: 0;
  font-size: 0.95rem;
}

/* Upgrade Prompt */
.upgrade-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 0.75rem;
  text-align: center;
}

.upgrade-icon {
  font-size: 2rem;
  color: #6B7280;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.upgrade-message {
  color: #9CA3AF;
  font-size: 0.95rem;
  margin: 0 0 1rem 0;
}

.upgrade-link {
  color: #7357D2;
  font-size: 0.9rem;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.upgrade-link:hover {
  color: #9B7EE8;
}

/* Log Type Toggles */
.log-type-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.log-type-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.log-type-toggle:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.log-type-toggle.active {
  background-color: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.4);
}

.log-type-toggle:disabled {
  cursor: default;
  opacity: 0.7;
}

.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.log-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
  text-transform: uppercase;
}

.log-type-toggle.active .log-type-name {
  color: #fff;
}

.log-type-count {
  font-size: 0.75rem;
  color: #6B7280;
}

.log-type-toggle.active .log-type-count {
  color: #9CA3AF;
}
</style>
